// @flow

import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useP2PSession } from '@alphaott/app-analytics'
import { getP2PStatsSnapshot } from '@alphaott/p2p'
import {
  isP2PAnalyticsEnabled as getIsP2PAnalyticsEnabled,
  getP2PAnalyticsInterval,
} from '@alphaott/smart-tv-config'

import { usePlayerContext } from '../store'

const BOUND_POLL_INTERVAL = 1000

const getFirstSourceUrl = (mediaStreams: ?Array<Object>): string =>
  mediaStreams?.[0]?.sources?.[0]?.src || ''

const getSnapshot = () => {
  const { peerCount, bytes } = getP2PStatsSnapshot()

  return { peers: peerCount, bytes }
}

type Params = {
  contentType: string,
  contentId: string,
  contentTitle: string,
  mediaStreams?: Array<Object>,
}

// eslint-disable-next-line complexity
export const useP2PHeartbeat = ({ contentType, contentId, contentTitle, mediaStreams }: Params) => {
  const [state] = usePlayerContext()
  const { playing, seeking, loading, stopped, src } = state

  const isAnalyticsEnabled = useSelector(getIsP2PAnalyticsEnabled)
  const analyticsInterval = useSelector(getP2PAnalyticsInterval)

  const [isP2PBound, setIsP2PBound] = useState(false)
  useEffect(() => {
    const intervalId = setInterval(() => {
      setIsP2PBound(getP2PStatsSnapshot().bound)
    }, BOUND_POLL_INTERVAL)

    return () => clearInterval(intervalId)
  }, [])

  const session = useP2PSession({
    contentType,
    contentId,
    contentTitle,
    isP2PActive: isP2PBound && isAnalyticsEnabled,
    getSnapshot,
    intervalMs: analyticsInterval * 1000,
  })

  const contentUrl = src || getFirstSourceUrl(mediaStreams)
  const isActivePlaying = Boolean(playing && !seeking && !loading)
  const prevStoppedRef = useRef(Boolean(stopped))

  useEffect(() => {
    if (contentUrl) {
      session.onSourceLoaded(contentUrl)
    }
  }, [contentUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    session.onActivePlayingChange(isActivePlaying)
  }, [isActivePlaying]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stopped && !prevStoppedRef.current) {
      session.onEnd()
    }
    prevStoppedRef.current = Boolean(stopped)
  }, [stopped]) // eslint-disable-line react-hooks/exhaustive-deps
}

export default useP2PHeartbeat
